class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector("div");
    this.closeButton = this.querySelector("#close-newsletter");
    this.init();
  }

  init() {
    // Check local storage to see if the pop-up should be shown
    if (localStorage.getItem("newsletterDismissed")) {
      this.popup.removeAttribute("open");
      return;
    }

    // if ?customer_posted=true#contact_form in the url dismiss the pop up
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("customer_posted") === "true") {
      this.popup.removeAttribute("open");
      localStorage.setItem("newsletterDismissed", "true");
      return;
    }

    // Automatically open the popup when the page loads
    setTimeout(() => {
      this.popup.setAttribute("open", "true");
    }, 500); // Delay slightly to ensure smooth transition

    // Close button logic
    this.closeButton.addEventListener("click", () => {
      this.popup.removeAttribute("open");
      // local storage after 5 seconds
      setTimeout(() => {
        localStorage.setItem("newsletterDismissed", "true");
      }, 500);
    });
  }
}

// Define the custom element
customElements.define("newsletter-popup", NewsletterPopup);

class AccordionElement extends HTMLElement {
  constructor() {
    super();
    this.details = this.closest("details");
    this.summary = this.details.querySelector("summary");

    this._resetHeight();

    // Bind event handlers
    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);

    // bind onclick to summary element
    this.summary.addEventListener("click", this.onClick);

    // Listen for window resize event
    window.addEventListener("resize", this.onResize);
  }

  _open() {
    this.details.setAttribute("data-opening", "true");
    this.details.setAttribute("open", "true");

    this.details.style.height = `${
      this.summary.scrollHeight + this.scrollHeight
    }px`;
  }

  _resetHeight() {
    this.details.style.height = `${this.summary.scrollHeight}px`;
  }

  _close() {
    this.details.removeAttribute("data-opening");
    this.details.setAttribute("data-closing", "true");

    setTimeout(() => {
      this.details.removeAttribute("open");
      this.details.removeAttribute("data-closing");
    }, 300);

    this.details.style.height = `${this.summary.scrollHeight}px`;
  }

  onClick(e) {
    e.preventDefault();
    if (!this.details.open) {
      this._open();
    } else {
      this._close();
    }
  }

  onResize() {
    if (this.details.open) {
      this.details.style.height = `${
        this.summary.scrollHeight + this.scrollHeight
      }px`;
    } else {
      this._resetHeight();
    }
  }

  // Clean up event listener when element is removed from the DOM
  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
  }
}

if (!customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}

class QuickAddToCart extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.getAttribute("data-variant-id");
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  async onClick(e) {
    e.preventDefault();
    const res = await fetch("/cart/add", {
      method: "post",
      body: new URLSearchParams({
        id: this.variantId,
        quantity: 1,
      }),
    });

    // update cart
    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const cartDrawer = document.getElementById("cart");
    cartDrawer.innerHTML = html.querySelector("#cart").innerHTML;

    cartDrawer.openDrawer();
  }
}

if (!customElements.get("quick-add")) {
  customElements.define("quick-add", QuickAddToCart);
}

class SizeChart extends HTMLElement {
  constructor() {
    super();

    this.sizeChart = JSON.parse(this.getAttribute("data-size-chart") || "[]");

    this.container = this.querySelector(".chart");
    this.toggleBtn = this.querySelector(".main-btn");
    this.inchesBtn = this.querySelector(".inches");
    this.cmBtn = this.querySelector(".centimeters");

    this.shadow = this.container.attachShadow({ mode: "open" });

    this.renderTable();
    this.bindToggle();
    this.bindUnitSwitch();

    // Load unit preference from localStorage
    const savedUnit = localStorage.getItem("unit") || "in";
    this.setUnit(savedUnit);
  }

  renderTable() {
    const style = document.createElement("style");
    style.textContent = `
      * {
        font-family: "IBM Plex Sans", sans-serif;
        font-style: normal;
        font-weight: 400;
        font-size: 12px !important;
        line-height: 16px !important;
        leading-trim: both;
        letter-spacing: 0.04em;
        text-align: center;
        color: #095093;
      }
      .table-header,
      .table-header th {
        text-transform: lowercase;
      }
      table {
        width: 100%;
        border-radius: 8px;
        border-spacing: 0;
        border-collapse: collapse;
        padding: 10px;
        border: 1px solid #095093;
        table-layout: fixed;
      }
      th, td {
        width: auto;
        padding: 7px 4px;
        height: 32px;
        border: 1px solid #095093;
        word-break: break-word;
        text-align: center;
      }
    `;

    const table = this.generateTable();
    this.shadow.appendChild(style);
    this.shadow.appendChild(table);
  }

  generateTable() {
    const table = document.createElement("table");

    if (this.sizeChart.length > 0) {
      const headers = Object.values(this.sizeChart[0]);
      const thead = document.createElement("thead");
      thead.classList.add("table-header");

      const headerRow = document.createElement("tr");
      headers.forEach((text) => {
        const th = document.createElement("th");
        th.textContent = text;
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");
      this.sizeChart.slice(1).forEach((rowData) => {
        const row = document.createElement("tr");
        Object.values(rowData).forEach((cellData) => {
          const cell = document.createElement("td");
          cell.textContent = cellData;
          row.appendChild(cell);
        });
        tbody.appendChild(row);
      });

      table.appendChild(tbody);
    }

    return table;
  }

  bindToggle() {
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener("click", () => {
        this.toggleAttribute("open");
      });
    }
  }

  bindUnitSwitch() {
    if (!this.inchesBtn || !this.cmBtn) return;

    this.inchesBtn.addEventListener("click", () => {
      this.setUnit("in");
    });

    this.cmBtn.addEventListener("click", () => {
      this.setUnit("cm");
    });
  }

  setUnit(unit) {
    const table = this.container.shadowRoot.querySelector("table");
    if (!table) return;

    const rows = table.querySelectorAll("tbody tr");
    const isCM = unit === "cm";

    // Save preference
    localStorage.setItem("unit", unit);

    // Toggle active/visual states
    this.inchesBtn.classList.toggle("active", !isCM);
    this.cmBtn.classList.toggle("active", isCM);
    this.inchesBtn.classList.toggle("opacity-50", isCM);
    this.cmBtn.classList.toggle("opacity-50", !isCM);

    // Update cell values
    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll("td");
      cells.forEach((cell, colIndex) => {
        const key = Object.keys(this.sizeChart[0])[colIndex];
        const original = this.sizeChart[rowIndex + 1][key];
        const num = parseFloat(original);
        if (!isNaN(num)) {
          cell.textContent = isCM ? (num * 2.54).toFixed(1) : num.toString();
        }
      });
    });
  }
}

// Define the custom element if it's not already defined
if (!customElements.get("size-chart")) {
  customElements.define("size-chart", SizeChart);
}

class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);

    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    // get the menu button
    this.menuButton = this.querySelector("button[id='menu-button']");
    this.mobileMenu = this.querySelector("nav[id='mobile-menu']");
    this.closeButton = this.querySelector("button[id='close']");
    this.menuBg = this.querySelector("div[id='bg-menu']");

    console.log(this.menuButton);

    // bind the click event

    this.closeButton.addEventListener("click", this.onClick);
    this.menuButton.addEventListener("click", this.onClick);
  }

  onClick(e) {
    e.preventDefault();

    if (this.hasAttribute("open")) {
      document.body.style.overflow = "auto";
      this.closeMenu();
    } else {
      document.body.style.overflow = "hidden";
      this.openMenu();
    }
  }
  openMenu() {
    this.mobileMenu.setAttribute("open", "true");
    this.setAttribute("open", "true");
  }

  closeMenu() {
    this.removeAttribute("open");
    this.mobileMenu.removeAttribute("open");
  }
}

if (!customElements.get("mobile-menu")) {
  customElements.define("mobile-menu", MobileMenu);
}

class CartItem extends HTMLElement {
  constructor() {
    super();
    // bind click event to button id decrease
    this.decreaseButton = this.querySelector("button[id='decrease']");
    this.increaseButton = this.querySelector("button[id='increase']");
    this.quantity = parseInt(this.querySelector("p[id='quantity']").innerHTML);

    this.pid = this.getAttribute("data-product-id");
    this.handleQuantity = this.handleQuantity.bind(this);

    this.decreaseButton.addEventListener("click", this.handleQuantity);
    this.increaseButton.addEventListener("click", this.handleQuantity);
  }

  async handleQuantity(e) {
    console.log(e.target.id);
    if (e.target.id === "decrease") {
      if (parseInt(this.quantity) === 1) {
        // remove the item from the cart
        document.querySelector("cart-drawer").updateQuantity(this.pid, 0);
      } else if (parseInt(this.quantity) > 1) {
        document
          .querySelector("cart-drawer")
          .updateQuantity(this.pid, this.quantity - 1);
      }
    } else if (e.target.id === "increase") {
      document
        .querySelector("cart-drawer")
        .updateQuantity(this.pid, this.quantity + 1);
    }
    e.preventDefault();
    console.log(this.pid, this.quantity);
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

class SlideShow extends HTMLElement {
  constructor() {
    super();
    this.initSwiper();
  }

  initSwiper() {
    // Make sure Swiper only initializes when the swiper-container is present
    const swiperContainer = this;
    console.log(swiperContainer);
    if (swiperContainer) {
      const swiper = new Swiper(".swiper", {
        // Swiper options
        slidesPerView: 1, // Number of slides to show
        spaceBetween: 0, // Space between slides
        speed: 400,
        initialSlide: parseInt(this.getAttribute("initial-slide")) || 0,
        zoom: {
          maxRatio: 3,
        },
      });
      console.log(swiper);
    }
  }
}

if (!customElements.get("product-slideshow")) {
  customElements.define("product-slideshow", SlideShow);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);

    this.addEventListener("click", this.onClick);

    this.addVariantToCart = this.addVariantToCart.bind(this);
  }

  onClick(e) {
    // Close the cart if the user clicks outside of the cart
    const isCloseButton = e.target.closest("#close");

    if (e.target.id === "cart" || isCloseButton) {
      e.preventDefault();

      if (window.location.pathname === "/cart") {
        window.location.href = "/";
      } else {
        this.closeDrawer();
      }
    }
  }

  openDrawer() {
    this.setAttribute("open", "true");

    //disable scroll
    document.body.style.overflow = "hidden";
  }

  closeDrawer() {
    this.removeAttribute("open");

    //enable scroll
    document.body.style.overflow = "auto";
  }

  async updateQuantity(itemId, quantity) {
    await fetch("/cart/change", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: itemId,
        quantity: quantity,
      }),
    }).then((res) => {
      console.log(res);
    });
    // Update the cart drawer after the quantity change
    await this.update();
  }

  async update() {
    const res = await fetch("/?section_id=cart-drawer");
    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const cartDrawer = document.getElementById("cart");
    cartDrawer.innerHTML = html.querySelector("#cart").innerHTML;

    console.log(html);
  }

  async addToCart(form) {
    await fetch("/cart/add", {
      method: "post",
      body: new FormData(form),
    });
    // update cart
    await this.update().then(() => {
      this.openDrawer();
    });
  }

  async addVariantToCart(variantId, quantity = 1) {
    await fetch("/cart/add", {
      method: "post",
      body: new URLSearchParams({
        id: variantId,
        quantity: quantity,
      }),
    });
    // update cart
    await this.update().then(() => {
      this.openDrawer();
    });
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class HeaderCart extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  onClick(e) {
    e.preventDefault();
    document.querySelector("cart-drawer").openDrawer();
  }
}

if (!customElements.get("header-cart")) {
  customElements.define("header-cart", HeaderCart);
}

// Turn into a more abstracted component for the drawer
class RemoveButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }
  async onClick(e) {
    e.preventDefault();
    console.log(e.target.href);

    const form = this.closest("form");

    await fetch(e.target.href, {
      method: "post",
      body: new FormData(form),
    });

    document.querySelector("cart-drawer").update();
  }
}

if (!customElements.get("remove-button")) {
  customElements.define("remove-button", RemoveButton);
}

class Newsletter extends HTMLElement {
  constructor() {
    super();
    // elements are input with id email, button with id subscribe
    this.email = this.querySelector("input[id='email']");
    this.button = this.querySelector("button[id='subscribe']");
    this.onClick = this.onClick.bind(this);
    console.log(this.button);

    // if ?customer_posted=true#contact_form is in the url, show a success message
    const urlParams = new URLSearchParams(window.location.search);
    const customerPosted = urlParams.get("customer_posted");
    if (customerPosted === "true") {
      this.changeStatus("Thank you for subscribing");
    }
    this.button.addEventListener("click", this.onClick);
  }
  async onClick(e) {
    console.log(this.email.value);
    if (this.email.value.length < 5) {
      this.changeStatus("Please enter a valid email ");
    }
  }

  async changeStatus(text) {
    this.querySelector("p").textContent = text;
  }
}

if (!customElements.get("newsletter")) {
  customElements.define("newsletter-widget", Newsletter);
}
